/**
 * inclusions-helpers.js
 *
 * Shared utilities for working with scope item inclusions arrays.
 *
 * BACKGROUND: Inclusions arrays can contain EITHER plain strings OR
 * heading objects with shape { type: 'heading', text: '...' }.
 * Code that calls `.join()` or `String()` on these arrays without
 * handling heading objects produces "[object Object]" - a pervasive
 * bug class first identified in Run #1 and found in 10+ files.
 *
 * This module centralises the heading-object-safe logic so every
 * consumer can import it instead of repeating the inline ternary.
 *
 * @module inclusions-helpers
 */

/**
 * A single inclusion entry: either a plain string or an object that may carry
 * a `text` (heading objects: `{ type: 'heading', text: '...' }`) and/or a
 * `type` discriminator. Kept structural + permissive so callers passing richer
 * inclusion objects don't need to narrow.
 */
export type InclusionEntry =
  | string
  | { type?: string; text?: unknown; [key: string]: unknown }
  | null
  | undefined;

/**
 * Explicit allowance marker used by the Stage 4/5 editors.
 *
 * A normal inclusion is descriptive work inside its parent scope item's one
 * aggregate price. Only an operator-created allowance heading may expose a
 * separate figure, and that figure remains display-only (already contained in
 * the parent item's amount).
 */
export const INCLUSION_PRICING_KIND_ALLOWANCE = 'allowance' as const;

const CURRENCY_BEARING_TEXT_PATTERNS = [
  /[$€£]\s*\d/i,
  /\b(?:aud|usd|nzd|gbp|eur)\s*(?:[$€£]\s*)?\d/i,
  /\b\d(?:[\d,\s]*\d)?(?:\.\d{1,2})?\s*(?:aud|usd|nzd|gbp|eur|dollars?|bucks?)\b/i,
  // A bare figure is monetary when it immediately follows an allowance label.
  // Require a grouped or 3+ digit amount and reject measurement units so copy
  // such as "Allowance for 1200 mm pipe" remains valid descriptive scope.
  /\b(?:allowance|provisional\s+sum)\b\s*(?:(?:of|is)\s+)?(?::|-)?\s*(?:\d{1,3}(?:[,\s]\d{3})+|\d{3,})(?:\.\d{1,2})?(?![\d,.])(?!\s*(?:(?:mm|cm|m|metres?|meters?|kg|g|tonnes?|litres?|liters?|ml|m2|m²|m3|m³|sqm|sq\.?\s*m|percent)\b|%))/i,
  // GST wording immediately after a number also makes its financial meaning
  // explicit, even when the model omitted a currency symbol or code.
  /\b(?:\d{1,3}(?:[,\s]\d{3})+|\d+)(?:\.\d{1,2})?(?![\d,.])\s*(?:(?:\+|plus)\s*)?(?:(?:inc(?:l(?:uding|usive)?)?|ex(?:cl(?:uding|usive)?)?)\.?\s*)?gst\b/i,
];

/**
 * Extract the display text from a single inclusion entry.
 * - Strings are returned as-is (trimmed).
 * - Heading objects ({ type: 'heading', text: '...' }) return their `.text`.
 * - Nullish / other values return ''.
 *
 * @param inc - A single inclusion entry
 */
export function safeInclusionText(inc: InclusionEntry): string {
  if (inc == null) return '';
  if (typeof inc === 'string') return inc.trim();
  if (typeof inc === 'object' && inc.text) return String(inc.text).trim();
  return '';
}

/**
 * Return true only for a heading that deliberately represents a client-facing
 * allowance. A legacy heading `subtotal` is NOT enough: older QuoteMaker
 * versions used that field for section subtotals, whose financial meaning is
 * different from an allowance.
 */
export function isExplicitAllowanceHeading(inc: InclusionEntry): boolean {
  if (!inc || typeof inc !== 'object' || inc.type !== 'heading') return false;
  return String(inc.pricingKind ?? inc.pricing_kind ?? '').trim().toLowerCase() === INCLUSION_PRICING_KIND_ALLOWANCE;
}

/**
 * Read the optional display-only allowance amount. It never participates in
 * quote totals, GST, cost math, or margin calculations.
 */
export function getExplicitAllowanceAmount(inc: InclusionEntry): number | null {
  if (!isExplicitAllowanceHeading(inc) || !inc || typeof inc !== 'object') return null;
  const raw = inc.allowanceAmount ?? inc.allowance_amount ?? inc.subtotal;
  if (raw == null || raw === '') return null;
  const amount = Number(raw);
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
}

/**
 * Read a pre-GLD-014 section subtotal without reclassifying it as an
 * allowance. This is a display compatibility path only: the value remains
 * excluded from quote totals, GST, cost math and margin calculations.
 */
export function getLegacyHeadingSubtotal(inc: InclusionEntry): number | null {
  if (!inc || typeof inc !== 'object' || inc.type !== 'heading' || isExplicitAllowanceHeading(inc)) return null;
  if (inc.subtotal == null || inc.subtotal === '') return null;
  const amount = Number(inc.subtotal);
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
}

/**
 * Operator-owned inclusion pricing is display metadata, never AI-authored
 * scope copy. Both the explicit allowance marker and old section-subtotal
 * records need to survive Assistant rewrites and round-trips.
 */
export function hasOperatorInclusionPricingDisplay(inc: InclusionEntry): boolean {
  return isExplicitAllowanceHeading(inc) || getLegacyHeadingSubtotal(inc) !== null;
}

export function requiresInclusionPricingRevalidation(inc: InclusionEntry): boolean {
  return Boolean(inc && typeof inc === 'object' && inc.pricingRevalidationRequired === true);
}

/**
 * Return true for operator-owned pricing metadata that the Assistant must not
 * rename or delete. A cross-parent move clears the amount before setting the
 * revalidation marker, so that marker remains protected even though there is
 * deliberately no figure left to display.
 */
export function hasOperatorInclusionPricingControl(inc: InclusionEntry): boolean {
  return hasOperatorInclusionPricingDisplay(inc)
    || requiresInclusionPricingRevalidation(inc);
}

/**
 * Detect a monetary amount embedded in prose. Plain measurements and counts
 * remain valid (for example, "50 mm pipe"), while strings such as "$1,200",
 * "AUD 1,200", "1,200 dollars", "Allowance 1,200 + GST", and
 * "1,200 incl GST" are rejected at AI mutation boundaries.
 */
export function containsCurrencyAmount(value: unknown): boolean {
  const text = typeof value === 'string'
    ? value
    : (value && typeof value === 'object' && 'text' in value ? String((value as { text?: unknown }).text ?? '') : '');
  return CURRENCY_BEARING_TEXT_PATTERNS.some((pattern) => pattern.test(text));
}

/**
 * Store an operator-edited allowance amount in one canonical field. Historic
 * amount aliases are read for compatibility by getExplicitAllowanceAmount,
 * but every edit/clear removes them so a stale alias cannot resurrect an old
 * amount after the input is cleared.
 */
export function withExplicitAllowanceAmount(inc: InclusionEntry, amount: number | null): InclusionEntry {
  if (!isExplicitAllowanceHeading(inc) || !inc || typeof inc !== 'object') return inc;
  const next: { type?: string; text?: unknown; [key: string]: unknown } = {
    ...inc,
    type: 'heading',
    pricingKind: INCLUSION_PRICING_KIND_ALLOWANCE,
  };
  delete next.pricing_kind;
  delete next.allowanceAmount;
  delete next.allowance_amount;
  delete next.subtotal;
  delete next.pricingRevalidationRequired;
  if (amount != null && Number.isFinite(amount) && amount >= 0) next.subtotal = amount;
  return next;
}

/**
 * Keep inclusion-level disclosure amounts in the same project display basis as
 * their parent scope item when Stage 4 switches between inc- and ex-GST.
 *
 * QuoteMaker stores scope amounts in the active project display basis rather
 * than attaching an independent tax basis to each inclusion. Explicit
 * allowances therefore move with their parent amount. Historic heading
 * subtotals follow the same conversion for backwards-compatible display, but
 * remain legacy headings (they are not reclassified as editable allowances).
 * Revalidation markers are deliberately preserved.
 */
/**
 * Defend against IEEE-754 floating-point roundtrip drift when converting between
 * Ex-GST and Inc-GST (e.g. $170.00 / 1.1 = $154.55; $154.55 * 1.1 = $170.005 -> $170.01).
 * Snaps cents that are within 2c of a $10 increment (1000c) or 1c of a dollar increment (100c)
 * back to the clean boundary when converting to Inc-GST.
 */
function snapToNearestCleanIncGstCents(cents: number): number {
  if (!Number.isFinite(cents)) return cents;
  const roundedCents = Math.round(cents);
  // $10 boundary check (1000 cents)
  const rem10 = ((roundedCents % 1000) + 1000) % 1000;
  if (rem10 <= 2) return roundedCents - rem10;
  if (rem10 >= 998) return roundedCents + (1000 - rem10);

  // $1 boundary check (100 cents)
  const remDollar = ((roundedCents % 100) + 100) % 100;
  if (remDollar <= 1) return roundedCents - remDollar;
  if (remDollar >= 99) return roundedCents + (100 - remDollar);

  return roundedCents;
}

export function convertInclusionPricingDisplayBasis(
  inc: InclusionEntry,
  {
    fromGstInclusive,
    toGstInclusive,
  }: { fromGstInclusive: boolean; toGstInclusive: boolean },
): InclusionEntry {
  if (!inc || typeof inc !== 'object' || fromGstInclusive === toGstInclusive) return inc;

  const multiplier = toGstInclusive ? 1.1 : (1 / 1.1);
  const convertAmount = (amount: number): number => {
    const rawCents = (amount * multiplier) * 100;
    const finalCents = toGstInclusive ? snapToNearestCleanIncGstCents(rawCents) : Math.round(rawCents);
    return finalCents / 100;
  };

  if (isExplicitAllowanceHeading(inc)) {
    const amount = getExplicitAllowanceAmount(inc);
    if (amount === null) return inc;
    const needsRevalidation = requiresInclusionPricingRevalidation(inc);
    const converted = withExplicitAllowanceAmount(inc, convertAmount(amount));
    return needsRevalidation && converted && typeof converted === 'object'
      ? { ...converted, pricingRevalidationRequired: true }
      : converted;
  }

  const legacySubtotal = getLegacyHeadingSubtotal(inc);
  if (legacySubtotal === null) return inc;
  return {
    ...inc,
    subtotal: convertAmount(legacySubtotal),
  };
}

// Keys that record which GST basis an item's own amount is in. cost-math
// (via resolveScopePricingBasis) reads them before the display basis.
const SCOPE_PRICING_BASIS_STAMP_KEYS = [
  'pricingBasis', 'pricing_basis', 'amountMode', 'amount_mode', 'isGstInclusive',
  'quoted_amount_inc_gst', 'quoted_amount_ex_gst',
] as const;

/**
 * Re-stamp an item's own GST basis to `isGstInclusive`, with its quoted inc
 * and ex amounts recomputed from its current amount (a GST-exempt row's inc
 * equals its ex). An item that carries no basis stamp is returned as it is,
 * so unstamped items stay unstamped. Used wherever an amount is converted
 * or re-derived in the display basis: a stamp left in the old basis makes
 * cost-math and the re-estimate target read the amount in the wrong basis
 * (spec 2026-10-07 F50, QA round 1).
 */
export function restampScopeItemPricingBasis<T extends { [key: string]: unknown }>(item: T, isGstInclusive: boolean): T {
  if (!item || typeof item !== 'object') return item;
  if (!SCOPE_PRICING_BASIS_STAMP_KEYS.some((key) => Object.prototype.hasOwnProperty.call(item, key))) return item;
  const inclusive = isGstInclusive !== false;
  const next: { [key: string]: unknown } = {
    ...item,
    pricingBasis: inclusive ? 'inc_gst' : 'ex_gst',
    isGstInclusive: inclusive,
  };
  delete next.pricing_basis;
  delete next.amountMode;
  delete next.amount_mode;
  if (Object.prototype.hasOwnProperty.call(next, 'gstInclusive')) next.gstInclusive = inclusive;
  if (Object.prototype.hasOwnProperty.call(next, 'scopeGstInclusive')) next.scopeGstInclusive = inclusive;
  const amount = Number(String(item.amount ?? '').replace(/,/g, ''));
  if (String(item.amount ?? '').trim() !== '' && Number.isFinite(amount)) {
    const exempt = item.gstExempt === true || item.gst_exempt === true;
    const round = (value: number) => Math.round(value * 100) / 100;
    next.quoted_amount_inc_gst = inclusive || exempt ? round(amount) : round(amount * 1.1);
    next.quoted_amount_ex_gst = !inclusive || exempt ? round(amount) : round(amount / 1.1);
  }
  return next as T;
}

/**
 * Convert a complete scope price surface between GST display bases in one
 * operation. Parent amounts and every operator-owned inclusion disclosure
 * must move together; converting only one side makes the quote internally
 * contradictory even though its labels look valid.
 *
 * Amounts that cannot be parsed are preserved verbatim. Converted parent
 * amounts use the same two-decimal string representation as the Stage 4
 * editor, while inclusion metadata retains its existing canonical shape.
 */
export function convertScopePricingDisplayBasis<
  T extends { amount?: unknown; inclusions?: InclusionEntry[]; [key: string]: unknown },
>(
  scope: T[] | null | undefined,
  {
    fromGstInclusive,
    toGstInclusive,
  }: { fromGstInclusive: boolean; toGstInclusive: boolean },
): T[] {
  if (!Array.isArray(scope) || fromGstInclusive === toGstInclusive) return scope || [];

  const multiplier = toGstInclusive ? 1.1 : (1 / 1.1);
  const parseAmount = (value: unknown): number | null => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const match = String(value ?? '').replace(/,/g, '').match(/[-+]?\d+(?:\.\d+)?/);
    if (!match) return null;
    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const convertAmount = (amount: number): string => {
    const rawCents = (amount * multiplier) * 100;
    const finalCents = toGstInclusive ? snapToNearestCleanIncGstCents(rawCents) : Math.round(rawCents);
    return (finalCents / 100).toFixed(2);
  };

  return scope.map((item) => {
    if (!item || typeof item !== 'object') return item;
    const amount = parseAmount(item.amount);
    // A GST-exempt row (the HBCF premium) charges no GST, so its price is the
    // same number in both bases. Its amount and its inclusion money stay as
    // they are; converting them lowered a $950.00 premium to $863.64 on a
    // switch to ex GST (QA round 4).
    const exempt = item.gstExempt === true || item.gst_exempt === true;
    const inclusions = !exempt && Array.isArray(item.inclusions)
      ? item.inclusions.map((inc) => convertInclusionPricingDisplayBasis(inc, {
        fromGstInclusive,
        toGstInclusive,
      }))
      : item.inclusions;

    const converted = {
      ...item,
      ...(amount !== null && !exempt ? { amount: convertAmount(amount) } : {}),
      ...(Array.isArray(inclusions) ? { inclusions } : {}),
    };
    // The amount is now in the new basis, so the basis the item itself
    // carries (pricingBasis, isGstInclusive, quoted inc/ex) moves with it.
    return amount !== null ? restampScopeItemPricingBasis(converted, toGstInclusive) : converted;
  });
}

/**
 * An allowance/subtotal entered under one aggregate scope price is not
 * automatically valid under another. Keep the descriptive heading when a
 * human moves it between parents, but clear the display-only money so the
 * destination must be reviewed rather than silently inheriting stale pricing.
 */
export function prepareInclusionForCrossParentMove(inc: InclusionEntry): InclusionEntry {
  if (!inc || typeof inc !== 'object') return inc;
  if (isExplicitAllowanceHeading(inc)) {
    return {
      ...(withExplicitAllowanceAmount(inc, null) as Exclude<InclusionEntry, string | null | undefined>),
      pricingRevalidationRequired: true,
    };
  }
  if (getLegacyHeadingSubtotal(inc) !== null) {
    const next: { type?: string; text?: unknown; [key: string]: unknown } = {
      ...inc,
      pricingRevalidationRequired: true,
    };
    delete next.subtotal;
    return next;
  }
  return inc;
}

/**
 * Carry only operator-owned inclusion pricing controls into a newly-created
 * aggregate parent. The descriptive headings survive, but every old amount is
 * cleared and explicitly marked for human revalidation.
 */
export function prepareOperatorPricedInclusionsForNewParent(inclusions: unknown): InclusionEntry[] {
  if (!Array.isArray(inclusions)) return [];
  return (inclusions as InclusionEntry[])
    .filter((inc) => hasOperatorInclusionPricingControl(inc))
    .map((inc) => prepareInclusionForCrossParentMove(inc));
}

/**
 * Resolve a cross-parent pricing review by explicitly keeping the heading as
 * unpriced descriptive scope. For an old allowance this also removes the
 * allowance classification, so Stage 5/PDF cannot show an empty allowance.
 * Entering a new allowance amount uses withExplicitAllowanceAmount instead.
 */
export function resolveInclusionPricingRevalidationAsUnpriced(inc: InclusionEntry): InclusionEntry {
  if (!inc || typeof inc !== 'object') return inc;
  const next: { type?: string; text?: unknown; [key: string]: unknown } = { ...inc };
  delete next.pricingRevalidationRequired;
  delete next.pricingKind;
  delete next.pricing_kind;
  delete next.allowanceAmount;
  delete next.allowance_amount;
  delete next.subtotal;
  return next;
}

/**
 * Project an inclusion into the Stage 5 AI prompt without exposing any
 * per-inclusion money. The explicit allowance marker is retained so the model
 * can preserve the wording, while the operator-entered figure stays out of the
 * model context and out of generated cost lines.
 */
export function inclusionForAiPrompt(inc: InclusionEntry): string | { type: 'heading'; text: string; pricingKind?: 'allowance' } | null {
  const text = safeInclusionText(inc);
  if (!text) return null;
  if (inc && typeof inc === 'object' && inc.type === 'heading') {
    return {
      type: 'heading',
      text,
      ...(isExplicitAllowanceHeading(inc) ? { pricingKind: INCLUSION_PRICING_KIND_ALLOWANCE } : {}),
    };
  }
  return text;
}

/**
 * Accept client-copy structure from an AI response while stripping every
 * price-like field. AI may write inclusion text; it cannot author an
 * allowance amount or a per-inclusion price.
 */
export function normaliseAiAuthoredInclusion(inc: InclusionEntry): string | { type: 'heading'; text: string } | null {
  const text = safeInclusionText(inc);
  if (!text || containsCurrencyAmount(text)) return null;
  if (inc && typeof inc === 'object' && inc.type === 'heading') {
    return { type: 'heading', text };
  }
  return text;
}

function inclusionIdentity(inc: InclusionEntry): string {
  return safeInclusionText(inc).toLocaleLowerCase().replace(/\s+/g, ' ').trim();
}

function preservePricedHeadingCopy(
  priced: Exclude<InclusionEntry, string | null | undefined>,
  authored: string | { type: 'heading'; text: string },
): Exclude<InclusionEntry, string | null | undefined> {
  return {
    ...priced,
    type: 'heading',
    text: typeof authored === 'string' ? authored : authored.text,
  };
}

/**
 * Merge AI-authored copy with the current operator-owned pricing display.
 *
 * The model may reorder inclusions, but it cannot create, delete, rename or
 * alter an explicit allowance/legacy subtotal. Without a stable inclusion ID,
 * only exact heading identity is safe to match. Anything the AI renames or
 * omits is treated as unrelated copy and the operator-owned heading is
 * reinserted at its previous relative position.
 */
export function mergeAiAuthoredInclusionsPreservingOperatorPricing(
  authoredInclusions: unknown,
  existingInclusions: unknown,
): InclusionEntry[] {
  const authored = normaliseAiAuthoredInclusions(authoredInclusions);
  const existing = Array.isArray(existingInclusions) ? existingInclusions as InclusionEntry[] : [];
  const priced = existing
    .map((entry, index) => ({ entry, index, identity: inclusionIdentity(entry), used: false }))
    .filter(({ entry }) => hasOperatorInclusionPricingControl(entry));

  const merged: InclusionEntry[] = authored.map((entry) => {
    if (!entry || typeof entry !== 'object' || entry.type !== 'heading') return entry;
    const identity = inclusionIdentity(entry);
    const match = priced.find((candidate) => !candidate.used && candidate.identity === identity);
    if (!match || !match.entry || typeof match.entry !== 'object') return entry;
    match.used = true;
    return preservePricedHeadingCopy(match.entry, entry);
  });

  for (const candidate of priced.filter(({ used }) => !used).sort((a, b) => a.index - b.index)) {
    merged.splice(Math.min(candidate.index, merged.length), 0, candidate.entry);
  }
  return merged;
}

/**
 * Sanitize a complete AI-authored inclusion list at a mutation boundary.
 * Only plain text or the safe heading shape can cross into live quote state.
 */
export function normaliseAiAuthoredInclusions(inclusions: unknown): Array<string | { type: 'heading'; text: string }> {
  if (!Array.isArray(inclusions)) return [];
  return inclusions
    .map((inc) => normaliseAiAuthoredInclusion(inc as InclusionEntry))
    .filter((inc): inc is string | { type: 'heading'; text: string } => inc !== null);
}

/**
 * Join an inclusions array into a single string, safely handling
 * heading objects. Empty entries are filtered out.
 *
 * @param inclusions - Array of strings and/or heading objects
 * @param separator - Join separator
 */
export function safeJoinInclusions(inclusions: InclusionEntry[] | null | undefined, separator: string = ', '): string {
  if (!Array.isArray(inclusions)) return '';
  return inclusions
    .map(safeInclusionText)
    .filter(Boolean)
    .join(separator);
}

/**
 * Filter an inclusions array to only "real" inclusions (not heading objects).
 * Useful for counting or validating non-heading content.
 *
 * Note: the predicate is not a type guard and the original returns whatever
 * survives the filter, so the element type is preserved as `InclusionEntry`
 * rather than narrowed to `string` (the JSDoc `Array<string>` was aspirational
 * - a non-heading object with truthy text survives too). Runtime unchanged.
 *
 * @param inclusions
 */
export function filterRealInclusions(inclusions: InclusionEntry[] | null | undefined): InclusionEntry[] {
  if (!Array.isArray(inclusions)) return [];
  return inclusions.filter(inc => {
    if (inc && typeof inc === 'object' && inc.type === 'heading') return false;
    return inc && String(inc).trim();
  });
}
